import { AcceptedFileTypeExpanded } from '../shared/file';
import { IChannel } from './communityTypes';
import { OnboardingStatus } from './userTypes';

export interface INewMessageUserState {
  users: IMessageRoomParticipant[];
  hasMore: boolean;
  loading: boolean;
  page: number;
}

export interface IInitialChatState {
  chats: ICommunityMessage[];
  hasMore: boolean;
  loading: boolean;
}

export interface IReplyState {
  hasMore: boolean;
  loading: boolean;
  replies: ICommunityMessage[];
}

export interface ICommunityMessageSlice {
  rooms: ICommunityMessageRoom[];
  newMessageUserState: INewMessageUserState;
  chatRoomDetails: ICommunityMessageRoom | null;
  chatsState: IInitialChatState;
  touchedMessageData: ICommunityMessage | null;
  editMessageData: ICommunityMessage | null;
  replayMessageData: ICommunityMessage | null;
  currentChannelActiveUser: IMessageRoomParticipant[];
  replyState: IReplyState;
  // forDeleteSelectedMessage: ICommunityMessage | null;
}

export interface ChannelMember {
  _id: string;
  name: string;
  profilePicUrl: string;
  isOnline?: boolean;
  isCreator?: boolean;
}

export enum MessagesLeftPanelTabs {
  MESSAGE_ROOMS = 'message-rooms',
  NEW_MESSAGE_ROOM = 'new-message-room',
}

export type MessagesLeftPanelMode = `${MessagesLeftPanelTabs}`;

export enum MessageRoomRightPanelTabs {
  MESSAGE_ROOM_INFO = 'message-room-info',
  MESSAGE_ROOM_MEMBERS = 'message-room-members',
  MESSAGE_REPLIES_THREAD = 'message-replies-thread',
}

export type MessageRoomRightPanelMode = `${MessageRoomRightPanelTabs}`;

export interface MessagesFilters {
  search: string;
  type: MessageStatusType | string;
}

export type MessageStatusType = 'all' | 'unread';

export const CHAT_MEDIAS = ['doc', 'image', 'video', 'audio', 'poll'] as const;

export type ChatMediaType = (typeof CHAT_MEDIAS)[number];

export interface ICommunityMessageRoom {
  _id: string;
  communityOwner: string;
  roomType: MessageRoomType;
  participants: IMessageRoomParticipant[];
  channel: IChannel;
  unreadCount?: number;
  lastMessage?: ICommunityMessage;
  lastSeen?: string; // last seen message ID
  pinnedMessages?: ICommunityMessage[];
  messageNotificationsMuted?: boolean;
  selfStopReceivingMessages?: boolean;
  otherStopReceivingMessages?: boolean;
}

export type MessageRoomType = 'channel' | 'dm';

export interface IMessageRoomParticipant {
  _id: string;
  name: string;
  profilePicUrl: string;
  onboarding?: OnboardingStatus;
}

export interface ICommunityMessageThreadResult {
  unreadCount: number;
  replies: ICommunityMessage[];
  lastSeenReply?: string;
}

export interface PollOption {
  optionContent: string;
  optionNumber: number;
  optionId: string;
}

export interface IPollVotesPerOption {
  [key: string]: number;
}

export interface IPollMetadata {
  creator: string;
  question: string;
  pollOptions: PollOption[];
  pollEndAt: string;
  pollVotesPerOption: IPollVotesPerOption;
  isResultVisibleToMembers: boolean;
  allowVoteChange: boolean;
  pollStartAt: string;
}

export interface ICommunityMessage {
  _id: string;
  room: string;
  message: string;
  assets?: IMessageMediaContent[];
  reactions?: ICommunityMessageReactions;
  edited?: boolean;
  isDeleted?: boolean;
  lastEditedAt: string;
  isThreadRoot?: boolean;
  threadRoot?: string;
  createdAt: string;
  updatedAt: string;
  sender: ICommunityMessageSender;
  threadRootMetadata?: ICommunityMessageThreadStats;
  poll?: string;
  pollMetadata?: IPollMetadata;
  isNewMessage?: boolean;
}

export enum FileType {
  IMAGE = 'image',
  VIDEO = 'video',
  AUDIO = 'audio',
  DOC = 'doc',
}

export type SingleFileType = `${FileType}`;

export type FileAttachmentType =
  | SingleFileType
  | 'image-custom'
  | 'video-local'
  | 'video-thumbnail'
  | 'file'
  | 'more';

export interface IMessageMediaContent {
  asset: string;
  type: AcceptedFileTypeExpanded;
  contentUrl: string;
  fileName: string;
  fileSize: number;
  fileType: FileAttachmentType;
  fileKey: string;
}

export interface ICommunityMessageReactions extends Record<string, number> {}

export interface ICommunityMessageSender {
  _id: string;
  name: string;
  profilePicUrl: string;
  onboarding?: OnboardingStatus;
  reaction?: string;
}

export interface ICreateMessageReqBody {
  room: string;
  message: string;
  assets: string[];
  threadRoot?: string;
}

export interface ICommunityMessageThreadStats {
  totalReplies: number;
  lastRepliedAt: string;
  firstFiveRepliers?: ICommunityMessageSender[];
  lastReply: string;
}

export interface IPollOption {
  optionContent: string;
  optionNumber: number;
}

export interface IPoll {
  allowVoteChange: boolean;
  isResultVisibleToMembers: boolean;
  pollEndAt: string; // ISO string for date
  pollOptions: IPollOption[];
  question: string;
  endDate?: string; // send as pollEndAt when creating poll
}

export interface ICreatePollRequest {
  poll: IPoll;
  room: string;
  message: string;
}

export type MessagesListType =
  | 'before-message'
  | 'after-message'
  | 'around-message'
  | 'around-last-seen'
  | 'from-start'
  | 'latest';

export interface ICommunityPollVotesResult {
  votes: IPollVoteByUser[];
  totalVotes: number;
}

export interface IPollVoteByUser {
  _id: string;
  name: string;
  email: string;
  phone: number;
  profilePicUrl: string;
  pollOption: string;
  votedAt: string;
}
